import { useMutation } from "@pinia/colada";
import { CreateWeeklyLimit, useApi, WeeklyLimit } from "@soft-db/monitoring-api-client";
import {
	composeCacheEffects,
	useInvalidator,
	useQueryEnabled,
	useScopeDataUpdater,
	useScopedQuery,
} from "./query";
import { vibrationCriteriasKeys } from "./vibration-criteria-query";

// --- Keys ---

export const weeklyLimitsKeys = {
	root: ["weekly-limits"] as const,

	list: () => [...weeklyLimitsKeys.root, "list"] as const,

	bySite: (site: string) => [...weeklyLimitsKeys.list(), "site", site] as const,

	byId: (id: string) => [...weeklyLimitsKeys.root, "id", id] as const,
};

// --- Cache effects ---

function useInvalidateSiteLimits() {
	const invalidate = useInvalidator();

	return (wl: WeeklyLimit) => {
		if (wl.site) {
			invalidate({
				keys: [weeklyLimitsKeys.bySite(wl.site)],
			});
		}
	};
}

function useUpdateCacheById() {
	const update = useScopeDataUpdater();

	return (wl: WeeklyLimit) => {
		update(vibrationCriteriasKeys.byId(wl.id), wl);
	};
}

function useWeeklyLimitCacheEffects() {
	return composeCacheEffects(useInvalidateSiteLimits(), useUpdateCacheById());
}

// --- Queries ---

export function useSiteWeeklyLimits(site: MaybeRefOrGetter<string>) {
	const api = useApi().weeklyLimit();

	const { data: weeklyLimits, ...rest } = useScopedQuery({
		key: () => weeklyLimitsKeys.bySite(toValue(site)),
		query: () => api.fetchWeeklyLimits(toValue(site)),
		enabled: useQueryEnabled(site),
	});

	return {
		weeklyLimits,
		data: weeklyLimits,
		...rest,
	};
}

export function useWeeklyLimit(id: MaybeRefOrGetter<string>) {
	const api = useApi().weeklyLimit();

	const { data: weeklyLimit, ...rest } = useScopedQuery({
		key: () => weeklyLimitsKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return {
		weeklyLimit,
		data: weeklyLimit,
		...rest,
	};
}

// --- Mutations ---

export function useCreateWeeklyLimit() {
	const api = useApi().weeklyLimit();

	return useMutation({
		mutation: ({ site, data }: { site: string; data: CreateWeeklyLimit }) => api.create(site, data),
		onSuccess: useWeeklyLimitCacheEffects(),
	});
}

export function useUpdateWeeklyLimit() {
	const api = useApi().weeklyLimit();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: CreateWeeklyLimit }) => api.update(id, data),
		onSuccess: useWeeklyLimitCacheEffects(),
	});
}

export function useDeleteWeeklyLimit() {
	const api = useApi().weeklyLimit();

	return useMutation({
		mutation: async (id: string) => {
			const wl = await api.fetch(id);
			await api.delete(id);

			return wl;
		},
		onSuccess: useInvalidateSiteLimits(),
	});
}
